import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { releaseNoteApi, wikiArticleApi } from '../utils/api';
import { useAuth } from '../lib/security';
import { ArrowLeft, FileText, Bug, ArrowUp, Plus, Search, BookOpen } from 'lucide-react';

interface SearchResultItem {
    type: 'release-note' | 'wiki-article';
    id: string;
    title: string;
    subtitle: string;
    description: string;
    icon: React.ReactNode;
    link: string;
    tags?: string[];
}

const getNoteTypeIcon = (type: number) => {
    switch (type) {
        case 1: return <Bug className="w-4 h-4 text-red-500 shrink-0" />;
        case 2: return <ArrowUp className="w-4 h-4 text-blue-500 shrink-0" />;
        case 3: return <Plus className="w-4 h-4 text-green-500 shrink-0" />;
        default: return <FileText className="w-4 h-4 text-gray-400 shrink-0" />;
    }
};

const SearchResultsPage: React.FC = () => {
    const { query } = useParams<{ query: string }>();
    const navigate = useNavigate();
    const [results, setResults] = useState<SearchResultItem[]>([]);
    const { canRead } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const doSearch = useCallback(async (searchQuery: string) => {
        if (!searchQuery.trim()) {
            setResults([]);
            setError(null);
            return;
        }

        setLoading(true);
        setError(null);

        // Only the areas this person can read: asking for the other half would
        // be refused, and a 403 is not a search result.
        const [releaseResult, wikiResult] = await Promise.all([
            canRead('releaseNotes')
                ? releaseNoteApi.search(searchQuery)
                : Promise.resolve({ data: [], error: undefined }),
            canRead('wiki')
                ? wikiArticleApi.search(searchQuery)
                : Promise.resolve({ data: [], error: undefined }),
        ]);

        const searchResults: SearchResultItem[] = [];

        // Process release note results
        if (!releaseResult.error && releaseResult.data) {
            for (const note of releaseResult.data) {
                const releaseVersion = (note as any).ReleaseVersion || '';
                const jiraKey = note.JiraKey || note.jiraKey || '';
                const type = (note.Type ?? note.type) || 0;
                const typeLabel = type === 1 ? 'Bug' : type === 2 ? 'Improvement' : type === 3 ? 'Feature' : '';

                searchResults.push({
                    type: 'release-note',
                    id: note.Id || note.id,
                    title: note.Title || note.title || 'Untitled',
                    subtitle: `${releaseVersion}${jiraKey ? ` · ${jiraKey}` : ''}${typeLabel ? ` · ${typeLabel}` : ''}`,
                    description: note.Description || note.description || '',
                    icon: getNoteTypeIcon(type),
                    link: `/releases?noteId=${note.Id || note.id}`,
                    tags: note.Package ? [note.Package] : undefined,
                });
            }
        }

        // Process wiki article results
        if (!wikiResult.error && wikiResult.data) {
            for (const article of wikiResult.data) {
                searchResults.push({
                    type: 'wiki-article',
                    id: article.id || article.Id,
                    title: article.title || article.Title || 'Untitled',
                    subtitle: article.tocName || article.tocDescription || 'Documentation',
                    description: article.content || article.Content || '',
                    icon: <BookOpen className="w-4 h-4 text-blue-500 shrink-0" />,
                    link: `/wiki/article/${article.id || article.Id}`,
                    tags: article.tags ? article.tags.split(',').map((tag: string) => tag.trim()).filter(Boolean) : undefined,
                });
            }
        }

        setResults(searchResults);
        setLoading(false);
    }, [canRead]);

    useEffect(() => {
        if (query) {
            doSearch(query);
        }
    }, [query, doSearch]);

    const getHighlightedText = (text: string, searchQuery: string): React.ReactNode => {
        if (!searchQuery || !text) return text;
        const idx = text.toLowerCase().indexOf(searchQuery.toLowerCase());
        if (idx === -1) return text;

        return (
            <>
                {text.substring(0, idx)}
                <mark className="bg-yellow-200 text-inherit rounded px-0.5">
                    {text.substring(idx, idx + searchQuery.length)}
                </mark>
                {getHighlightedText(text.substring(idx + searchQuery.length), searchQuery)}
            </>
        );
    };

    const truncateDescription = (text: string, maxLength: number = 160): string => {
        const plainText = text.replace(/<[^>]*>/g, '').replace(/&[^;]+;/g, '');
        if (plainText.length <= maxLength) return plainText;
        return plainText.substring(0, maxLength).trim() + '...';
    };

    return (
        <div className="max-w-4xl mx-auto bx-gutter py-8 fade-in">
            {/* Header */}
            <div className="mb-8">
                <button
                    onClick={() => navigate(-1)}
                    className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-4 transition-colors"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Back
                </button>
                <div className="flex items-center gap-3">
                    <Search className="w-6 h-6 text-slate-400" />
                    <h1 className="text-2xl font-bold text-slate-800">
                        Search results
                    </h1>
                    {query && (
                        <span className="text-lg text-slate-500 font-normal">
                            for &quot;<span className="font-medium text-slate-700">{query}</span>&quot;
                        </span>
                    )}
                </div>
                {results.length > 0 && (
                    <p className="text-sm text-slate-500 mt-1">
                        Found {results.length} result{results.length !== 1 ? 's' : ''}
                    </p>
                )}
            </div>

            {/* Error */}
            {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm mb-6">
                    {error}
                </div>
            )}

            {/* Loading */}
            {loading && (
                <div className="flex items-center justify-center py-16">
                    <div className="spinner"></div>
                </div>
            )}

            {/* Results */}
            {!loading && results.length > 0 && (
                <div className="space-y-4">
                    {results.map((result, index) => (
                        <Link
                            key={`${result.type}-${result.id}-${index}`}
                            to={result.link}
                            className="block bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md hover:border-slate-300 transition-all group"
                        >
                            <div className="flex items-start gap-3">
                                <div className="shrink-0 mt-0.5">
                                    {result.icon}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                            result.type === 'release-note'
                                                ? 'bg-purple-50 text-purple-700'
                                                : 'bg-blue-50 text-blue-700'
                                        }`}>
                                            {result.type === 'release-note' ? 'Release Note' : 'Documentation'}
                                        </span>
                                    </div>
                                    <h3 className="text-base font-semibold text-slate-800 group-hover:text-blue-600 transition-colors">
                                        {query ? getHighlightedText(result.title, query) : result.title}
                                    </h3>
                                    <p className="text-sm text-slate-500 mb-2">
                                        {query ? getHighlightedText(result.subtitle, query) : result.subtitle}
                                    </p>
                                    {result.description && (
                                        <p className="text-sm text-slate-600 line-clamp-2">
                                            {truncateDescription(result.description)}
                                        </p>
                                    )}
                                    {result.tags && result.tags.length > 0 && (
                                        <div className="flex gap-1 mt-2">
                                            {result.tags.map((tag, i) => (
                                                <span key={i} className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <svg className="w-5 h-5 text-slate-300 group-hover:text-slate-500 transition-colors shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </div>
                        </Link>
                    ))}
                </div>
            )}

            {/* No results */}
            {!loading && results.length === 0 && query && (
                <div className="text-center py-16">
                    <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-600 mb-2">No results found</h3>
                    <p className="text-sm text-slate-400">
                        Try different keywords or check your spelling.
                    </p>
                </div>
            )}

            {/* Empty state when no query */}
            {!loading && !query && (
                <div className="text-center py-16">
                    <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-600 mb-2">Enter a search query</h3>
                    <p className="text-sm text-slate-400">
                        Use the search bar above to find release notes and documentation.
                    </p>
                </div>
            )}
        </div>
    );
};

export default SearchResultsPage;
